import * as RSel from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import { forwardRef, type ComponentPropsWithoutRef } from 'react';
import { cn } from './cn';

/*
  Select — выбор одного из > 5 значений (до 5 — Radio/Segmented). Триггер выглядит как Input (h-9, md),
  список — слой z-popover с анимацией pop через data-state.
*/

export const Select = RSel.Root;
export const SelectGroup = RSel.Group;
export const SelectValue = RSel.Value;

export const SelectTrigger = forwardRef<HTMLButtonElement, ComponentPropsWithoutRef<typeof RSel.Trigger>>(
  ({ className, children, ...rest }, ref) => (
    <RSel.Trigger
      ref={ref}
      data-slot="select-trigger"
      className={cn(
        'flex h-9 w-full items-center justify-between gap-2 rounded-md border border-line bg-card px-3 text-sm text-fg',
        'transition-colors duration-fast hover:border-line-strong',
        'focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:outline-none',
        'disabled:cursor-not-allowed disabled:opacity-50 data-[placeholder]:text-fg-subtle',
        className,
      )}
      {...rest}
    >
      {children}
      <RSel.Icon asChild>
        <ChevronDown className="size-4 shrink-0 text-fg-subtle" />
      </RSel.Icon>
    </RSel.Trigger>
  ),
);
SelectTrigger.displayName = 'SelectTrigger';

export const SelectContent = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof RSel.Content>>(
  ({ className, children, position = 'popper', sideOffset = 6, ...rest }, ref) => (
    <RSel.Portal>
      <RSel.Content
        ref={ref}
        position={position}
        sideOffset={sideOffset}
        data-slot="select-content"
        className={cn(
          'z-popover max-h-80 min-w-(--radix-select-trigger-width) overflow-hidden rounded-md border border-line bg-elevated p-1 shadow-lg',
          'data-[state=closed]:animate-pop-out data-[state=open]:animate-pop-in',
          className,
        )}
        {...rest}
      >
        <RSel.Viewport>{children}</RSel.Viewport>
      </RSel.Content>
    </RSel.Portal>
  ),
);
SelectContent.displayName = 'SelectContent';

export const SelectItem = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof RSel.Item>>(
  ({ className, children, ...rest }, ref) => (
    <RSel.Item
      ref={ref}
      data-slot="select-item"
      className={cn(
        'relative flex cursor-pointer items-center gap-2 rounded-xs py-1.5 pr-8 pl-2 text-sm text-fg outline-none select-none',
        'transition-colors duration-fast data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[highlighted]:bg-hover',
        className,
      )}
      {...rest}
    >
      <RSel.ItemText>{children}</RSel.ItemText>
      <RSel.ItemIndicator className="absolute right-2 grid size-4 place-items-center text-brand-solid">
        <Check size={14} />
      </RSel.ItemIndicator>
    </RSel.Item>
  ),
);
SelectItem.displayName = 'SelectItem';
